import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  ENVIRONMENT_CONFIG,
  SettingsService,
  SettingsSlice
} from '@ipreo/northstar';
import { Observable, of, EMPTY } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { AllotmentPreferencesService } from '../../services/allotment-preferences.service';
import { AllotmentPreferences } from '../../types/allotment-preferences.types';
import { PlatformType } from '../../types/muni-environment';
import {
  SaveUserPreferenceFail,
  SetAllotmentPreferences
} from '../user-preferences/actions';
import {
  AllotmentPreferencesActionTypes,
  SaveAllotmentPreferences
} from './actions';

const toAllotmentPreferences = (
  setting: SettingsSlice['setting']
): AllotmentPreferences | null => {
  if (!isAllotmentPreferencesRecord(setting)) {
    return null;
  }

  return setting as unknown as AllotmentPreferences;
};

const isAllotmentPreferencesRecord = (
  setting: SettingsSlice['setting']
): setting is Record<string, unknown> => {
  if (!setting || Array.isArray(setting) || typeof setting !== 'object') {
    return false;
  }

  const candidate = setting as Record<string, unknown>;

  return (
    typeof candidate.displayNotifications === 'boolean' &&
    !!candidate.colWidths &&
    !Array.isArray(candidate.colWidths) &&
    typeof candidate.colWidths === 'object' &&
    !!candidate.gridColumnsOrder &&
    !Array.isArray(candidate.gridColumnsOrder) &&
    typeof candidate.gridColumnsOrder === 'object' &&
    Array.isArray(candidate.customViewColumnGroupings)
  );
};

@Injectable()
export class AllotmentPreferencesEffects {
  private readonly platform: PlatformType = inject(ENVIRONMENT_CONFIG).params
    .platform as PlatformType;

  public syncAllotmentPreferencesFromSettings$: Observable<SetAllotmentPreferences> =
    createEffect(() =>
      this.platform === PlatformType.buyside
        ? EMPTY
        : this.settingsService.get('allotment-preferences').pipe(
          filter((settingsSlice: SettingsSlice) => settingsSlice.loaded),
          map(
            (settingsSlice: SettingsSlice) =>
              new SetAllotmentPreferences(
                toAllotmentPreferences(settingsSlice.setting)
              )
          )
        )
    );

  public saveAllotmentPreferences$: Observable<
    SetAllotmentPreferences | SaveUserPreferenceFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AllotmentPreferencesActionTypes.Save),
      switchMap((action: SaveAllotmentPreferences) =>
        this.allotmentPreferencesService.save(action.payload).pipe(
          map(
            (preferences: AllotmentPreferences) =>
              new SetAllotmentPreferences(preferences)
          ),
          catchError((error: Error) =>
            of(new SaveUserPreferenceFail(error && error.message, 'allotments'))
          )
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly allotmentPreferencesService: AllotmentPreferencesService,
    private readonly settingsService: SettingsService
  ) { }
}
